// OverlayCanvas.jsx
//
// Mount <OverlayCanvasProvider> once, at the layout root — as a sibling of
// the sidebar/header, NOT nested inside .dashboardContent or .mainContent.
//
// Why this needs to exist at all:
//   .dashboardContent has `overflow-y: hidden` and .mainContent scrolls
//   independently. `position: fixed` children currently escape that
//   clipping only because no ancestor sets transform/filter/perspective/
//   will-change — any of those (a page-transition wrapper, a blur-on-open
//   effect, a sticky-header animation) would silently change the
//   containing block and break every sheet/modal nested inside it, with
//   no error, just a modal that quietly stops covering the screen.
//
//   Any overlay content — bottom sheets, drawers, modals, toasts — should
//   render into this canvas via <OverlayPortal> instead of inline where
//   it's declared. That decouples it permanently from wherever it happens
//   to sit in the component tree.
//
// Z-index scale used by anything rendered through this canvas:
//   1050  fixed page-level controls (FAB, etc.) — just above Bootstrap's
//         own .overlay (1040) so nothing from this app's Bootstrap
//         components can sit on top of it by accident
//   1100  sheet/modal backdrop + panel
//   1200  toasts / anything that must sit above an open sheet
// Keep new overlay z-index values inside this scale.

import React, { createContext, useContext, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const OverlayCanvasContext = createContext(null);

export function OverlayCanvasProvider({ children }) {
  const nodeRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  useLayoutEffect(() => {
    setMounted(true);
  }, []);

  return (
    <OverlayCanvasContext.Provider value={mounted ? nodeRef.current : null}>
      {mounted && children}
      <div ref={nodeRef} id="overlay-canvas" />
    </OverlayCanvasContext.Provider>
  );
}

export function useOverlayCanvasNode() {
  return useContext(OverlayCanvasContext);
}

/** Renders its children into the overlay canvas instead of in-place. */
export function OverlayPortal({ children }) {
  const node = useOverlayCanvasNode();
  return createPortal(children, node || document.body);
}